import { LoadScriptProps, useLoadScript } from '@react-google-maps/api';
import { formatCounty } from '@upstart/harmony/lib/formatter';
import useSWR from 'swr';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { useEffect } from 'react';
import {
  getStreetAddressFromGeocoderAddressComponents,
  getCityFromGeocoderAddressComponents,
  getStateFromGeocoderAddressComponents,
  getZipCodeFromGeocoderAddressComponents,
  getCountyFromGeocorderAddressComponents,
} from './utils';

const googleMapsLibraries: LoadScriptProps['libraries'] = ['places'];

export const useAddress = (streetAddress: string) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '',
    libraries: googleMapsLibraries,
  });

  const {
    setValue,
    suggestions: { data: addressSuggestions },
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['address'],
      componentRestrictions: { country: ['US'] },
    },
  });

  useEffect(() => {
    if (isLoaded) {
      setValue(streetAddress);
    }
  }, [streetAddress]);

  return useSWR(streetAddress, async () => {
    const options = await Promise.all(
      addressSuggestions.map(async suggestion => {
        const {
          structured_formatting: structuredFormatting,
          place_id: placeId,
        } = suggestion;
        const {
          main_text: mainText,
          secondary_text: secondaryText,
        } = structuredFormatting;

        const [{ address_components: result }] = await getGeocode({
          placeId,
        });

        const data = {
          streetAddress: getStreetAddressFromGeocoderAddressComponents(result),
          apt: '',
          city: getCityFromGeocoderAddressComponents(result),
          state: getStateFromGeocoderAddressComponents(result),
          zip: getZipCodeFromGeocoderAddressComponents(result),
          county: formatCounty(getCountyFromGeocorderAddressComponents(result)),
        };

        return {
          description: suggestion.description,
          mainText,
          secondaryText,
          data,
        };
      }),
    );
    return { options };
  });
};
